import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import NewObjectForm from './NewObjectForm';
import spinnerTitle from 'components/others/spinnerTitle';
import { useEffect, useState } from 'react';
import SvgSave from 'components/svg/SvgSave';
import * as apiLead from 'api/apiLeads';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import vconst from 'utils/helpers/structs/vconst';
import { useNavigate } from 'react-router-dom';
import { useSettings } from 'context/SettingsContext';
import type { LeadProposalGenerationType } from '@econtractor/types';
import type { NewObjectType } from '../types';
import { useNeedPay } from 'modules/admin';
import { syncNewObjectPriceFromProposal } from '../helpers/proposalAmountToObjectPrice';

export interface NewObjectModalProps {
    newObject: NewObjectType | null;
    setNewObject: React.Dispatch<React.SetStateAction<NewObjectType | null>>;
    acceptedGeneration?: LeadProposalGenerationType | null;
    acceptedLoading?: boolean;
    onRefreshAccepted?: () => void | Promise<void>;
}

const NewObjectModal = ({
    newObject,
    setNewObject,
    acceptedGeneration = null,
    acceptedLoading = false,
    onRefreshAccepted,
}: NewObjectModalProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { getSettings, localSettings } = useSettings();
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();
    const { needPay } = useNeedPay();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const hasActiveProposal = Boolean(acceptedGeneration);
    const leadId = Number(newObject?.leadId ?? 0);

    useEffect(() => {
        if (!leadId || !acceptedGeneration) return;
        setNewObject((prev) => {
            if (!prev?.leadId) return prev ?? null;
            return syncNewObjectPriceFromProposal(prev, acceptedGeneration, rate);
        });
    }, [acceptedGeneration, leadId, rate, setNewObject]);

    const handleClickCreate = async () => {
        if (!newObject) return;
        if (needPay()) return;
        setCreating(true);

        if (leadId > 0) {
            const eventData = {
                eventInfo: newObject?.info || '',
                statusID: vconst.leadAccept,
            };
            const resEvent = await apiLead.addEvent(leadId, eventData);
            if (!resEvent) {
                showToast(t('common:messError'), 'danger');
                return setCreating(false);
            }
        }

        const resObject = await apiObject.createObject(newObject as any);

        await getSettings();
        setCreating(false);

        if (resObject) {
            showToast(t('messObjectCreated'), 'success');
            navigate(`/objects/${resObject}`);
        } else showToast(t('common:messError'), 'danger');
    };

    return (
        <Modal
            show={newObject !== null}
            onHide={() => setNewObject(null)}
            onShow={() => {
                setCreating(false);
                void onRefreshAccepted?.();
            }}
            size="xl"
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton className="border-bottom">
                <Modal.Title>{t('title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {newObject ? (
                    <NewObjectForm
                        newObject={newObject}
                        setNewObject={setNewObject}
                        hasActiveProposal={hasActiveProposal}
                        acceptedLoading={acceptedLoading}
                    />
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button className="me-1" variant="secondary" onClick={() => setNewObject(null)}>
                    {t('common:buttons.close')}
                </Button>
                <Button
                    variant="info"
                    type="submit"
                    disabled={!newObject?.objectPrice || !(newObject?.objectName || '').trim() || creating}
                    onClick={handleClickCreate}
                >
                    {spinnerTitle(
                        <>
                            <SvgSave className="me-2 mtop-2" />
                            {t('common:buttons.save')}
                        </>,
                        creating,
                        t('common:buttons.saving'),
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default NewObjectModal;
